import {
  type AttendanceToday,
  type PunchDirection,
  type PunchItem,
  type PunchMode,
  formatDate,
  formatTime,
} from '@thrivehrm/shared';
import { Building2, House, LogOut, RotateCw } from 'lucide-react';
import * as React from 'react';
import { Badge } from '@/components/ui/badge';
import { Banner } from '@/components/ui/banner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError } from '@/lib/api';
import { notify } from '@/lib/toast';
import { useAttendanceToday, usePunch } from './api';
import { type NoFixReason, readFix, useLocationPermission } from './geolocation';
import { PUNCH_FLAG_LABELS, PUNCH_MODE_LABELS } from './labels';

type Phase = 'locating' | 'saving';

const NO_FIX: Readonly<Record<NoFixReason, string>> = {
  denied: 'Location is blocked for ThriveHRM',
  unavailable: 'Your location could not be found',
  timeout: 'Finding your location took too long',
  unsupported: 'This browser cannot share your location',
};

/** "Checked in at 9:52 am · Office" for the latest punch, or the empty state. */
function statusLine(last: PunchItem | undefined): string {
  if (!last) return 'You have not checked in today.';
  const verb = last.direction === 'IN' ? 'Checked in' : 'Checked out';
  return `${verb} at ${formatTime(last.punchedAt)} · ${PUNCH_MODE_LABELS[last.mode]}`;
}

/** Flags as badges; an office punch inside shows "Inside office". */
function PunchBadges({ punch }: { punch: PunchItem }) {
  return (
    <span className="flex flex-wrap gap-1">
      {punch.geofence === 'INSIDE' ? <Badge tone="success">Inside office</Badge> : null}
      {punch.flags.map((f) => (
        <Badge key={f} tone="warning">
          {PUNCH_FLAG_LABELS[f]}
        </Badge>
      ))}
    </span>
  );
}

/**
 * Check in / out (FR-ATT-3, PRD US-A1/A2, ADR-081): today's status, the next action — *Check in*
 * as Office or WFH, or *Check out* — and, on the page, today's punches with their flags. The
 * location is read only when a button is tapped; without it the punch is still sent (the API
 * flags it when Settings require a location). The server records the time.
 */
export function TodayPunchPanel({ showPunches = false }: { showPunches?: boolean }) {
  const today = useAttendanceToday();
  const punch = usePunch();
  const permission = useLocationPermission();
  const [phase, setPhase] = React.useState<Phase | null>(null);
  const [problem, setProblem] = React.useState<string | null>(null);
  const [noFix, setNoFix] = React.useState<NoFixReason | null>(null);

  if (today.isPending) return <Skeleton className="h-24 w-full" />;
  if (today.error) {
    return (
      <Banner
        tone="danger"
        title="Could not load today’s attendance."
        action={
          <Button size="sm" onClick={() => void today.refetch()} loading={today.isRefetching}>
            <RotateCw aria-hidden="true" />
            Retry
          </Button>
        }
      />
    );
  }
  const data: AttendanceToday = today.data;
  const last = data.punches.at(-1);
  const lastIn = data.punches.findLast((p) => p.direction === 'IN');

  async function send(direction: PunchDirection, mode: PunchMode) {
    setProblem(null);
    setNoFix(null);
    setPhase('locating');
    try {
      const located = await readFix();
      if (!located.fix) setNoFix(located.reason);
      setPhase('saving');
      const saved = await punch.mutateAsync({ direction, mode, ...(located.fix ?? {}) });
      const flags = saved.flags.map((f) => PUNCH_FLAG_LABELS[f]).join(', ');
      notify.success(
        `${direction === 'IN' ? 'Checked in' : 'Checked out'} at ${formatTime(saved.punchedAt)}`,
        flags ? { description: `HR will review it: ${flags}.` } : undefined,
      );
    } catch (err: unknown) {
      setProblem(err instanceof ApiError ? err.message : 'Could not save your punch. Try again.');
    } finally {
      setPhase(null);
    }
  }

  const busy = phase !== null;
  const busyLabel = phase === 'locating' ? 'Finding your location…' : 'Saving…';

  return (
    <div className="flex flex-col gap-4">
      <p className="text-body text-foreground" aria-live="polite">
        {statusLine(last)}
      </p>
      <div className="flex flex-wrap gap-2">
        {data.nextDirection === 'IN' ? (
          <>
            <Button
              onClick={() => void send('IN', 'OFFICE')}
              disabled={busy}
              className="max-md:flex-1"
            >
              <Building2 aria-hidden="true" />
              Check in · Office
            </Button>
            <Button
              variant="secondary"
              onClick={() => void send('IN', 'WFH')}
              disabled={busy}
              className="max-md:flex-1"
            >
              <House aria-hidden="true" />
              Check in · WFH
            </Button>
          </>
        ) : (
          <Button
            onClick={() => void send('OUT', lastIn?.mode ?? 'OFFICE')}
            disabled={busy}
            className="max-md:flex-1"
          >
            <LogOut aria-hidden="true" />
            Check out
          </Button>
        )}
      </div>
      {busy ? (
        <p className="text-small text-muted-foreground" role="status">
          {busyLabel}
        </p>
      ) : null}
      {problem ? (
        <Banner tone="danger" live>
          {problem}
        </Banner>
      ) : null}
      {noFix && !busy ? (
        <Banner tone="warning" live>
          {NO_FIX[noFix]}, so your punch was saved without it
          {data.requireLocation ? ' and HR will review it' : ''}.
          {noFix === 'denied' ? ' Allow location for this site in your browser settings.' : ''}
        </Banner>
      ) : permission === 'denied' && data.requireLocation ? (
        <Banner tone="warning">
          Location is blocked for ThriveHRM. Your punches are still saved but HR will review them —
          allow location for this site in your browser settings.
        </Banner>
      ) : permission === 'prompt' ? (
        <p className="text-small text-muted-foreground">
          Your browser asks for your location when you check in or out. It is read only at that
          moment, never in the background.
        </p>
      ) : null}
      {data.nextDirection === 'IN' && !data.wfhApproved ? (
        <p className="text-small text-muted-foreground">
          No approved WFH today — a WFH check-in is flagged for HR.
        </p>
      ) : null}
      {showPunches && data.punches.length > 0 ? (
        <section aria-labelledby="punches-heading" className="flex flex-col gap-2">
          <h3 id="punches-heading" className="text-h3 text-foreground">
            Today’s punches
          </h3>
          <ol className="flex flex-col divide-y rounded-lg border">
            {data.punches.map((p) => (
              <li key={p.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3">
                <span className="tabular w-20 text-body-strong text-foreground">
                  {formatTime(p.punchedAt)}
                </span>
                <span className="text-foreground">
                  {p.direction === 'IN' ? 'Check in' : 'Check out'} · {PUNCH_MODE_LABELS[p.mode]}
                </span>
                <PunchBadges punch={p} />
              </li>
            ))}
          </ol>
        </section>
      ) : null}
    </div>
  );
}

/** `/me/attendance`: the panel in a card titled with today's date. */
export function TodayPunchCard() {
  return (
    <Card variant="raised" aria-labelledby="today-heading" data-slot="today-card">
      <CardHeader
        title={<span id="today-heading">Today</span>}
        description={formatDate(new Date())}
      />
      <CardContent>
        <TodayPunchPanel showPunches />
      </CardContent>
    </Card>
  );
}

/** Dashboard widget (ARCHITECTURE §8.4): check in / out without leaving the dashboard. */
export function AttendanceTodayWidget() {
  return <TodayPunchPanel />;
}
